import { MODIFIED_PROPS, modifyProps } from '../../generators/modifyProps';

import { JDSAvatarProps } from './generated';
import { MouseEvent } from 'react';

const legacySizes = ['x-small', 'x-large', 'xx-large'] as const;
type legacySizesType = (typeof legacySizes)[number];

export interface AvatarProps
  extends Omit<JDSAvatarProps, 'size' | 'image' | 'kind' | 'onClick'> {
  size?: legacySizesType | JDSAvatarProps['size'];
  kind?: 'default' | 'initials' | 'image';
  disabled?: boolean;
  initials?: string;
  image?:
    | React.ReactElement<React.ImgHTMLAttributes<HTMLImageElement>>
    | string;
  isClickable?: boolean;
  onClick?: (ev: MouseEvent<HTMLDivElement>) => void;
}

export const PROP_MAP = (props: AvatarProps): MODIFIED_PROPS<AvatarProps> => {
  if (legacySizes.includes(props.size as legacySizesType)) {
    console.warn(
      'these sizes are not deprecated. use sizes starting with `xtra`'
    );
  }
  if (typeof props.image === 'object') {
    console.warn('Image as an element is deprecated. Pass a string instead');
  }
  return {
    size: {
      properties: {
        size: props.size
      },
      defaultValue: 'medium',
      variations: {
        'x-small': {
          size: 'xtra-small'
        },
        small: {
          size: 'small'
        },
        medium: {
          size: 'medium'
        },
        large: {
          size: 'large'
        },
        'x-large': {
          size: 'xtra-large'
        },
        'xx-large': {
          size: 'xtra-xtra-large'
        }
      }
    },
    kind: {
      properties: {
        kind: props.kind
      },
      defaultValue: 'default',
      variations: {
        default: {
          icon: 'ic_profile'
        }
      }
    },
    onClick: {
      properties: {
        onClick: props.onClick
      }
    }
  };
};

export const extractInitials = (value = 'A', size: string) => {
  const trimmedValue = value?.trim();

  if (size === 'xtra-small') return (trimmedValue[0] || 'A').toUpperCase();
  if (trimmedValue.indexOf(' ') >= 0) {
    const strMatch = trimmedValue?.trim().match(/\b(\w)/g) || [''];
    const strJoin = strMatch.join('') || trimmedValue;
    return strJoin.toUpperCase().trim().substring(0, 2);
  }
  return trimmedValue.toUpperCase().trim().substring(0, 2);
};

export const withLogic =
  (Component: React.ComponentType<AvatarProps>): React.FC<AvatarProps> =>
  (userProps: AvatarProps) => {
    const props = modifyProps(userProps, PROP_MAP) as AvatarProps;
    return (
      <Component
        {...props}
        image={
          typeof props.image === 'string'
            ? props.image
            : props.image?.props?.src
        }
        initials={extractInitials(props.initials, props.size || '')}
      />
    );
  };
